import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom'; // Added for navigation

// Define the structure of a blog post item based on your API response
interface BlogPostItem {
  slug: string;
  title: string;
  // Assuming your API might return a short summary or you might derive it
  summary?: string; // Optional: if your API provides a summary
  content?: string; // Full content, might be fetched separately or with list
  imageUrl?: string; // Optional
  createdAt: string; // Assuming ISO string date
  metaTitle?: string;
  metaDescription?: string;
}

const BlogPage = () => {
  const [posts, setPosts] = useState<BlogPostItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      setError(null);
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) {
          throw new Error("API URL is not defined. Please check your environment variables.");
        }
        const response = await fetch(`${apiUrl}/posts`);
        if (!response.ok) {
          throw new Error(`Failed to fetch posts: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        // Assuming the API returns an array of posts directly
        // If it's nested, like { items: [...] }, adjust accordingly, e.g., data.items
        setPosts(data.items || data); 
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred");
        console.error("Error fetching blog posts:", err);
      }
      setLoading(false);
    };

    fetchPosts();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Blog - Kult.io</title>
        <meta name="description" content="Read the latest articles and insights from the Kult.io team on AWS, cloud technology, and more." />
      </Helmet>
      <h1 className="text-4xl font-extrabold text-white sm:text-5xl mb-10 text-center">
        Kult.io Blog
      </h1>

      {loading && (
        <div className="text-center text-gray-300">
          <p>Loading posts...</p>
          {/* You can add a spinner here */}
        </div>
      )}

      {error && (
        <div className="text-center text-red-400 bg-red-900/30 p-4 rounded-md">
          <p>Error loading posts: {error}</p>
        </div>
      )}

      {!loading && !error && posts.length === 0 && (
         <div className="text-center text-gray-400">
          <p>No blog posts found yet. Check back soon!</p>
        </div>
      )}

      {!loading && !error && posts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post) => (
            <div key={post.slug} className="bg-gray-800 p-6 rounded-lg border border-gray-700 flex flex-col justify-between hover:shadow-xl transition-shadow duration-300">
              <div>
                {post.imageUrl && (
                  <img src={post.imageUrl} alt={post.title} className="w-full h-48 object-cover rounded-md mb-4" />
                )}
                <h2 className="text-2xl font-semibold text-white mb-2">{post.title}</h2>
                <p className="text-gray-400 text-sm mb-2">
                  Published on: {new Date(post.createdAt).toLocaleDateString()}
                </p>
                <p className="text-gray-300 mb-4 line-clamp-3">
                  {/* Use summary if available, otherwise create a snippet from content */}
                  {post.summary || (post.content ? `${post.content.substring(0, 150)}...` : 'No summary available.')}
                </p>
              </div>
              <Link 
                to={`/blog/${post.slug}`} 
                className="text-blue-400 hover:text-blue-300 font-medium self-start mt-auto">
                Read more &rarr;
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogPage; 